import React, { useState } from "react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { InfoWindow, Button } from "_styleguide";
import { Button as BarnButton, message } from "@centrate-io/barn";
import { useSettings } from "_contexts/useSettings";
import IconSVG from "_assets/logos/icon.svg";
import { useApolloClient } from "@apollo/client";
import { getAgreementData } from "_graphql/queries";
import { createSubscription } from "_graphql/mutations";
import { getErrors } from "_helpers/api";
import { useNavigate } from "react-router-dom";
import AgreementViewer from "./_components/AgreementViewer";
import { createAgreementElement } from "./_helpers/agreementHelper";
import {
  generatePDFBlob,
  blobToBase64,
  downloadBlob,
} from "./_helpers/pdfHelper";
import { useLead } from "_contexts/useLead";
import { isDarkColor } from "_helpers/colors";
import { useStats } from "_contexts/useStats";
import { getAgreementKey } from "_helpers/agreement";
import { useGTM } from "_hooks/useGTM";
import "./ConfirmPopup.scss";

function ConfirmPopup(props) {
  const { pageSettings } = props;
  const { settings } = useSettings();
  const navigate = useNavigate();
  const client = useApolloClient();
  const { lead, plaid, reload } = useLead();
  const [agreement, setAgreement] = useState();
  const [agreementKey, setAgreementKey] = useState(getAgreementKey());
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { updateStats } = useStats();
  const { gtmEvent, gtmError } = useGTM();

  const primaryColor = settings?.primary_color || "#0f766e";
  const primaryFontColor = isDarkColor(primaryColor) ? "#fff" : "#000";

  const getAgreement = async () => {
    const stateResponse = await client.query({
      query: getAgreementData,
      variables: {
        application_id: lead?.id,
      },
    });
    setAgreement(stateResponse?.data?.getAgreementData);
    if (!agreementKey) {
      setAgreementKey(
        stateResponse?.data?.getAgreementData?.parent_company?.agreement_key
      );
    }
  };

  const saveSubscription = async (base64PDF) => {
    let formResult = null;
    try {
      formResult = await client.mutate({
        variables: {
          application_id: lead.id,
          pdf_content: base64PDF,
          from_apply: true,
        },
        mutation: createSubscription,
      });
      gtmEvent("subscription_created");
      message.success("Congratulations, you are all set up!");
      await reload();
      await updateStats({
        application_id: lead.id,
        self_enrollment_id: settings.id,
        page: props?.settings?.name,
        drop_off_page: props?.settings?.next || props?.settings?.name,
      });
      navigate("/success");
    } catch (err) {
      formResult = null;
      gtmError("subscription_failed", {
        error: getErrors(err),
      });
      message.error(getErrors(err) || "Error subscribing, try again");
    }
    return formResult ? true : false;
  };

  let plaidData = null;
  try {
    plaidData = plaid ? JSON.parse(plaid) : null;
  } catch (err) {
    console.log(err);
  }

  const downloadPDF = async () => {
    setDownloading(true);

    try {
      const pdfDocument = createAgreementElement(agreementKey, {
        form: lead,
        plaidData: plaidData?.metadata,
        agreement,
      });

      const pdfBlob = await generatePDFBlob(pdfDocument);
      const filename = `Letter_of_Engagement_${Date.now()}.pdf`;
      downloadBlob(pdfBlob, filename);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    } finally {
      setDownloading(false);
    }
  };

  const generatePDF = async () => {
    setSaving(true);

    try {
      const pdfDocument = createAgreementElement(agreementKey, {
        form: lead,
        plaidData: plaidData?.metadata,
        agreement,
      });

      const pdfBlob = await generatePDFBlob(pdfDocument);
      const base64PDF = await blobToBase64(pdfBlob);

      await saveSubscription(base64PDF);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    getAgreement();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const popupContent = (
    <InfoWindow className="confirm-iw" onClose={props.closeWindow}>
      <img src={settings?.logo_url || IconSVG} alt="" />
      {isMobile ? (
        <div
          className="pp-agreement-scroll"
          dangerouslySetInnerHTML={{ __html: pageSettings.agreement_content }}
        />
      ) : (
        <div className="pp-terms-scroll">
          <AgreementViewer
            agreementKey={agreementKey}
            form={lead}
            plaidData={plaidData?.metadata}
            agreement={agreement}
          />
        </div>
      )}
      <div className="confirm-actions">
        <BarnButton
          size="large"
          onClick={downloadPDF}
          block
          type="secondary"
          loading={downloading}
        >
          Download
        </BarnButton>
        <Button
          disabled={!agreement}
          onClick={generatePDF}
          block
          size="large"
          style={{ background: primaryColor, color: primaryFontColor }}
          loading={saving}
        >
          {pageSettings.agreement_button_text}
        </Button>
      </div>
    </InfoWindow>
  );

  return createPortal(popupContent, document.body);
}

export default ConfirmPopup;
